// Task Editor 저장소·캐시·정규화.
// 정규화 자체는 `taskEditorUtils.migrateTaskEditorStateFromLegacy` 가 책임.
// 이 파일은 캐시·publish·draft 흐름만 다룬다.

import {
  migrateTaskEditorStateFromLegacy,
  TaskEditorState,
} from "./taskEditorUtils";

export interface PublishedTaskEditorTask extends TaskEditorState {
  versionId: string;
  updatedAt: string;
  updatedByUserId?: number;
}

// ─── 정규화 ───────────────────────────────────────────────────────────────

export function normalizeTaskEditorState(
  value: TaskEditorState | unknown
): TaskEditorState {
  return migrateTaskEditorStateFromLegacy(value);
}

export function isTaskEditorState(value: unknown): value is TaskEditorState {
  if (!value || typeof value !== "object") return false;
  const c = value as Record<string, unknown>;
  return (
    typeof c.task_id === "string" &&
    typeof c.title === "string" &&
    Array.isArray(c.blocks)
  );
}

export function normalizePublishedTaskEditorTask(
  value: unknown
): PublishedTaskEditorTask | undefined {
  if (!value || typeof value !== "object") return undefined;
  const c = value as Record<string, unknown>;
  if (typeof c.updatedAt !== "string") return undefined;
  const normalized = normalizeTaskEditorState(value);
  if (!normalized.task_id) return undefined;
  return {
    ...normalized,
    updatedAt: c.updatedAt,
    versionId: typeof c.versionId === "string" ? c.versionId : c.updatedAt,
    ...(typeof c.updatedByUserId === "number" &&
    Number.isFinite(c.updatedByUserId)
      ? { updatedByUserId: Math.floor(c.updatedByUserId as number) }
      : {}),
  };
}

export function normalizePublishedTaskEditorTasks(
  tasks: unknown
): PublishedTaskEditorTask[] {
  if (!Array.isArray(tasks)) return [];
  return tasks
    .map((t) => normalizePublishedTaskEditorTask(t))
    .filter((t): t is PublishedTaskEditorTask => Boolean(t));
}

function sortPublishedTaskEditorTasks(tasks: PublishedTaskEditorTask[]) {
  return [...tasks].sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
}

// ─── 캐시 ─────────────────────────────────────────────────────────────────

let taskEditorPublishedTasksCache: PublishedTaskEditorTask[] = [];
let taskEditorWorkingDraftCache: TaskEditorState | undefined;

export function setPublishedTaskEditorTasksCache(tasks: unknown) {
  taskEditorPublishedTasksCache = sortPublishedTaskEditorTasks(
    normalizePublishedTaskEditorTasks(tasks)
  );
}

export function setTaskEditorWorkingDraftCache(draft?: unknown) {
  if (!draft || typeof draft !== "object") {
    taskEditorWorkingDraftCache = undefined;
    return;
  }
  taskEditorWorkingDraftCache = normalizeTaskEditorState(draft);
}

export function ensureTaskEditorPublishedTasksSeeded() {
  // Server API + Recoil 기반으로 전환됨. 백엔드 시드 의존.
}

export function getPublishedTaskEditorTasks(): PublishedTaskEditorTask[] {
  return sortPublishedTaskEditorTasks(taskEditorPublishedTasksCache);
}

export function getPublishedTaskEditorTask(taskId: string) {
  const id = taskId.trim();
  if (!id) return undefined;
  return getPublishedTaskEditorTasks().find((t) => t.task_id === id);
}

export function getPublishedTaskEditorTitle(taskId: string) {
  return getPublishedTaskEditorTask(taskId)?.title;
}

export function isPublishedTaskEditorTaskIdTaken(
  taskId: string,
  currentTaskId?: string
) {
  const id = taskId.trim();
  if (!id) return false;
  const currentId = currentTaskId?.trim();
  return getPublishedTaskEditorTasks().some(
    (t) => t.task_id === id && t.task_id !== currentId
  );
}

export function savePublishedTaskEditorTask(
  state: TaskEditorState,
  currentTaskId?: string
): PublishedTaskEditorTask {
  const normalized = normalizeTaskEditorState(state);
  const versionTimestamp = new Date().toISOString();
  const published: PublishedTaskEditorTask = {
    ...normalized,
    task_id: normalized.task_id || "NEW-TASK-ID",
    title: normalized.title || "새 Task 제목",
    versionId: versionTimestamp,
    updatedAt: versionTimestamp,
  };
  const currentId = currentTaskId?.trim();

  const others = getPublishedTaskEditorTasks()
    .filter((t) => t.task_id !== published.task_id && t.task_id !== currentId)
    .map((t) => {
      if (
        published.is_key_activity === true &&
        published.set_number !== undefined &&
        t.set_number === published.set_number &&
        t.is_key_activity === true
      ) {
        return { ...t, is_key_activity: undefined };
      }
      return t;
    });

  setPublishedTaskEditorTasksCache([published, ...others]);
  return published;
}

export function removePublishedTaskEditorTask(taskId: string) {
  const id = taskId.trim();
  setPublishedTaskEditorTasksCache(
    getPublishedTaskEditorTasks().filter((t) => t.task_id !== id)
  );
}

export function saveTaskEditorWorkingDraft(state: TaskEditorState) {
  setTaskEditorWorkingDraftCache(state);
}

export function getTaskEditorWorkingDraft() {
  return taskEditorWorkingDraftCache
    ? normalizeTaskEditorState(taskEditorWorkingDraftCache)
    : undefined;
}
